import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LineDot } from "../../../../../../assets/icons/LineDot";
import { StyledLegend } from "../../../../../../components/CustomChartLegend/CustomChartLegend.styles";
import { useMultipleMeasurements } from "../../../../../../hooks/queries/measurements";
import { useUserPreferences } from "../../../../../../hooks/useUserPreferences";
import { userService } from "../../../../../../services/userService";
import { getFriendlySourceName } from "../../../../../../utils/chartSourceUtils";
import { formatMeasurementAverageRangeDates } from "../../../../../../utils/chartUtils";
import { getShortFormattedDate } from "../../../../../../utils/dateFormatter";
import {
  HeaderContainer,
  MeasurementContainer,
  Subtitle,
  Title,
} from "../../MeasurementModal.styles";
import {
  DATE_FILTER_ALL,
  DATE_FILTER_CUSTOM,
  DateFilterOptionType,
} from "../CustomOptionsBar";
import { SubtitleConatiner } from "../MeasurementSection/MeasurementSection.styles";
import { ModalMeasurementsTable } from "../ModalMeasurementsTable/ModalMeasurementsTable";
import { ModalSkeleton } from "../ModalSkeleton";
import {
  aggregateStepsByWeek,
  buildStepsChartData,
  getStepsSourceColor,
  StepsBarChart,
} from "../StepsBarChart";
import { StepsOptionsBar } from "./StepsOptionsBar";
import {
  DateLabel,
  Divider,
  LegendContainer,
  MainValueContainer,
  MainValuesContainer,
  NumberLabel,
  SourcesColumn,
  TextLabel,
} from "./StepsSection.styles";

type StepsSectionProps = {
  patientId?: string;
  cardTitle: string;
  unit?: string;
  isPinnedGraph?: boolean;
  onSelectedTableView?: (showTable: boolean) => void;
};

export function StepsSection({
  patientId,
  cardTitle,
  unit,
  isPinnedGraph,
  onSelectedTableView,
}: StepsSectionProps) {
  const { t } = useTranslation();

  const { getDateRangeFilter, portalPreferences, changePortalPreferences } =
    useUserPreferences();

  const dateRangeFilter = getDateRangeFilter();

  const [showTable, setShowTable] = useState(false);

  const { data, refetch, isFetching, isRefetching } = useMultipleMeasurements({
    patientId,
    zoomedVersion: true,
    types: ["daily_steps"],
    toDate: dateRangeFilter.endDate.format(),
    fromDate: dateRangeFilter.startDate.format(),
  });

  const allMeasurements = useMemo(() => {
    const map = new Map<string, any[]>();

    (data ?? []).forEach((d: any) => {
      if (!d?.measurements) return;
      d.measurements.forEach((measurementsMap: any, key: string) => {
        const source = (key || "unknown").toLowerCase();
        map.set(`${d.type}|${source}`, measurementsMap);
      });
    });

    return map;
  }, [data]);

  const mainValues = useMemo(() => {
    const firstResult = (data ?? []).find(
      (d: any) => d?.mainMeasurementValues
    ) as any;
    return (
      firstResult?.mainMeasurementValues ?? {
        min: null,
        max: null,
        average: null,
      }
    );
  }, [data]);

  const dailyData = useMemo(
    () => buildStepsChartData(allMeasurements),
    [allMeasurements]
  );

  // For ranges > 6 months, aggregate daily steps into weekly averages so the
  // chart stays readable. ALL has a nominal 1900->now range so use the data
  // span instead of the filter span.
  const weeklyAggregation = useMemo(() => {
    if (dailyData.rows.length === 0) return false;
    if (dateRangeFilter.id === DATE_FILTER_ALL) {
      const minTs = Math.min(...dailyData.rows.map((r) => r.timeValue));
      const maxTs = Math.max(...dailyData.rows.map((r) => r.timeValue));
      return dayjs(maxTs).diff(dayjs(minTs), "month") > 6;
    }
    return dateRangeFilter.endDate.diff(dateRangeFilter.startDate, "month") > 6;
  }, [dailyData.rows, dateRangeFilter]);

  const { rows: chartData, sources } = useMemo(
    () => (weeklyAggregation ? aggregateStepsByWeek(dailyData) : dailyData),
    [dailyData, weeklyAggregation]
  );

  const isEmpty = chartData.length === 0;

  useEffect(() => {
    refetch();
  }, [
    portalPreferences.dateRangeFilter,
    portalPreferences.customFromDate,
    portalPreferences.customToDate,
  ]);

  const handleFilterChange = (selectedDateFilter: DateFilterOptionType) => {
    const { id } = selectedDateFilter;
    if (id === DATE_FILTER_CUSTOM || id !== dateRangeFilter.id) {
      changePortalPreferences({
        ...portalPreferences,
        dateRangeFilter: id,
        customFromDate:
          id === DATE_FILTER_CUSTOM ? selectedDateFilter.startDate : undefined,
        customToDate:
          id === DATE_FILTER_CUSTOM ? selectedDateFilter.endDate : undefined,
      });

      if (id !== DATE_FILTER_CUSTOM) {
        userService.updateDateRangeFilter(id);
      }
    }
  };

  const handleTableViewChange = (enableTableView: boolean) => {
    setShowTable(enableTableView);
    onSelectedTableView?.(enableTableView);
  };

  const formatStepValue = (value: number | null | undefined) =>
    value != null ? Math.round(value).toLocaleString() : "-";

  // For "ALL" the filter spans 1900 -> now; show the actual data range when
  // there is data, and an empty subtitle when there is none (instead of the
  // ugly "Dec 31, 1899 - <today>").
  let displayDateRange: { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs } | null;
  if (dateRangeFilter.id === DATE_FILTER_ALL) {
    if (chartData.length > 0) {
      displayDateRange = {
        startDate: dayjs(Math.min(...chartData.map((d) => d.timeValue))),
        endDate: dayjs(Math.max(...chartData.map((d) => d.timeValue))),
      };
    } else {
      displayDateRange = null;
    }
  } else {
    displayDateRange = {
      startDate: dateRangeFilter.startDate,
      endDate: dateRangeFilter.endDate,
    };
  }

  const formattedDateRange = displayDateRange
    ? `${getShortFormattedDate(
        displayDateRange.startDate.format()
      )} - ${getShortFormattedDate(displayDateRange.endDate.format())}`
    : "";

  const meanRange =
    mainValues.minUnarchivedDate && mainValues.maxUnarchivedDate
      ? formatMeasurementAverageRangeDates(
          mainValues.minUnarchivedDate,
          mainValues.maxUnarchivedDate
        )
      : "-";

  if (isFetching || isRefetching) {
    return <ModalSkeleton isPinnedGraph={isPinnedGraph} width={968} />;
  }

  return (
    <MeasurementContainer>
      <HeaderContainer style={{ marginBottom: showTable ? "0px" : "20px" }}>
        <Title>{cardTitle}</Title>
        <Subtitle>{formattedDateRange}</Subtitle>

        <SubtitleConatiner>
          <StepsOptionsBar
            onChange={handleFilterChange}
            onTableViewChange={handleTableViewChange}
            selectedFilterDate={dateRangeFilter}
            showTable={showTable}
            isEmpty={isEmpty}
          />
        </SubtitleConatiner>
      </HeaderContainer>

      {showTable ? (
        <ModalMeasurementsTable
          data={allMeasurements}
          measurementType="daily_steps"
          patientId={patientId}
          isPinnedGraph={isPinnedGraph}
          unit={unit}
        />
      ) : (
        <StepsBarChart
          data={chartData}
          sources={sources}
          dateInterval={dateRangeFilter}
          height="80%"
          width={isPinnedGraph ? "100%" : 968}
          isPinnedGraph={isPinnedGraph}
          isEmpty={isEmpty}
          weeklyAggregation={weeklyAggregation}
          legend={
            <LegendContainer>
              <MainValuesContainer>
                <MainValueContainer>
                  <NumberLabel disabled={isEmpty}>
                    {formatStepValue(mainValues.max)}
                  </NumberLabel>
                  <TextLabel disabled={isEmpty}>
                    {t("patientPage_measurementModal_legend_highest")}
                  </TextLabel>
                  <DateLabel disabled={isEmpty}>
                    {mainValues.maxValueDate
                      ? dayjs(mainValues.maxValueDate).format("MMM D, YYYY")
                      : "-"}
                  </DateLabel>
                </MainValueContainer>
                <MainValueContainer>
                  <NumberLabel disabled={isEmpty}>
                    {formatStepValue(mainValues.min)}
                  </NumberLabel>
                  <TextLabel disabled={isEmpty}>
                    {t("patientPage_measurementModal_legend_lowest")}
                  </TextLabel>
                  <DateLabel disabled={isEmpty}>
                    {mainValues.minValueDate
                      ? dayjs(mainValues.minValueDate).format("MMM D, YYYY")
                      : "-"}
                  </DateLabel>
                </MainValueContainer>
                <MainValueContainer>
                  <NumberLabel disabled={isEmpty}>
                    {formatStepValue(mainValues.average)}
                  </NumberLabel>
                  <TextLabel disabled={isEmpty}>
                    {t("patientPage_measurementModal_legend_mean")}
                  </TextLabel>
                  <DateLabel disabled={isEmpty}>{meanRange}</DateLabel>
                </MainValueContainer>
              </MainValuesContainer>

              <Divider />

              <SourcesColumn>
                {sources.map((source, idx) => (
                  <StyledLegend key={source}>
                    <LineDot fill={getStepsSourceColor(source, idx)} />
                    {getFriendlySourceName(source, t)}
                  </StyledLegend>
                ))}
              </SourcesColumn>
            </LegendContainer>
          }
        />
      )}
    </MeasurementContainer>
  );
}
